/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Heading, IconButton, IconClose, Label } from '@americanexpress/dls-react';
import PropTypes from 'prop-types';
import { useRef, useEffect, useState } from 'react';
import advFormStyles from '../../../styles/AdvancedFormSearch/AdvanceFormSearchStyles';
import AdvSavedSearch from './AdvSavedSearch';
import AdvUpdateSearch from './AdvUpdateSearch';

const AdvFormHeader = ({
  pageData,
  setPageData,
  toggleAdvFormSearch,
  enableSaveSearch,
  savedSearchList,
  showPopup,
  setShowPopup,
  onItemClick,
  handleFormReset,
  selectedItem,
  setSelectedItem,
}) => {
  const [showUpdateAnchor, setShowUpdateAnchor] = useState(false);
  const initialPageDataRef = useRef(null);
  const [showUpdatePopup, setShowUpdatePopup] = useState(false);

  // When selectedItem changes, store a snapshot of current pageData as initialPageData
  useEffect(() => {
    if (selectedItem) {
      initialPageDataRef.current = JSON.parse(JSON.stringify(pageData));
    } else {
      initialPageDataRef.current = null;
    }
    setShowUpdateAnchor(false);
  }, [selectedItem]);

  // Deep equality check for any value (primitive, array, object)
  function deepEqual(a, b) {
    if (a === b) return true;
    if (typeof a !== typeof b) return false;
    if (typeof a !== 'object' || a === null || b === null) return false;
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (!deepEqual(a[i], b[i])) return false;
      }
      return true;
    }
    if (Array.isArray(a) !== Array.isArray(b)) return false;
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    for (let key of keysA) {
      if (!deepEqual(a[key], b[key])) return false;
    }
    return true;
  }

  // Show anchor if pageData differs from initialPageData
  useEffect(() => {
    if (!selectedItem || !initialPageDataRef.current) {
      setShowUpdateAnchor(false);
      return;
    }
    const initialFields = initialPageDataRef.current.formFields || [];
    const pageFields = pageData.formFields || [];
    let isDifferent = false;
    if (initialFields.length !== pageFields.length) {
      isDifferent = true;
    } else {
      for (let i = 0; i < pageFields.length; i++) {
        const initialField = initialFields[i] || {};
        const pageField = pageFields[i] || {};
        const allKeys = Array.from(new Set([...Object.keys(initialField), ...Object.keys(pageField)]));
        for (let key of allKeys) {
          if (!deepEqual(initialField[key], pageField[key])) {
            isDifferent = true;
            break;
          }
        }
        if (isDifferent) break;
      }
    }
    setShowUpdateAnchor(isDifferent);
  }, [pageData, selectedItem]);

  return (
    <div css={advFormStyles['.formRow']} className="col-md-12 pad-0-lr">
      <div className="row margin-0-lr pad-1-t flex-justify-between flex-align-items-center">
        <div className="row margin-0-lr pad-1-t flex-justify-between flex-align-items-center">
          <Heading
            id="AdvFormHeader-formHeader"
            level={2}
            className="dls-gray-06"
            headingStyle="heading5"
          >
            {pageData.formHeader}
          </Heading>
          {enableSaveSearch && (
            <>
              <AdvSavedSearch
                pageData={pageData}
                setPageData={setPageData}
                savedSearchList={savedSearchList}
                showPopup={showPopup}
                setShowPopup={setShowPopup}
                onItemClick={onItemClick}
                handleFormReset={handleFormReset}
                selectedItem={selectedItem}
                setSelectedItem={setSelectedItem}
              />
              {selectedItem && (
                <>
                  <Heading headingStyle="heading2" css={{ fontWeight: 'normal' }}>
                    [ '{selectedItem?.searchName}' applied
                    <a
                      href="#"
                      aria-label="reset"
                      onClick={e => { e.preventDefault(); handleFormReset(); }}
                      style={{ margin: '0 6px', textDecoration: 'none', fontWeight: 'bold', cursor: 'pointer', fontSize: 'large' }}
                    >
                      ×
                    </a>
                    ]
                  </Heading>
                  {showUpdateAnchor && (
                    <AdvUpdateSearch
                      pageData={pageData}
                      savedSearchList={savedSearchList}
                      showPopup={showUpdatePopup}
                      setShowPopup={setShowUpdatePopup}
                      onItemClick={onItemClick}
                      handleFormReset={handleFormReset}
                      selectedItemName={selectedItem?.searchName}
                      selectedItemId={selectedItem?.id}
                      isIncludeDateTimeChecked={!!(selectedItem?.metaDataJson?.startEndDateTime)}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
        <IconButton
          id="AdvFormHeader-advSearchClose"
          styleType="tertiary"
          icon={<IconClose />}
          onClick={toggleAdvFormSearch}
        />
      </div>
    </div>
  );
};

AdvFormHeader.displayName = 'AdvFormHeader';

AdvFormHeader.propTypes = {
  pageData: PropTypes.shape({
    formHeader: PropTypes.string,
    formFields: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
      })
    ),
  }),
  setPageData: PropTypes.func.isRequired,
  toggleAdvFormSearch: PropTypes.func.isRequired,
  savedSearchList: PropTypes.shape([]),
  enableSaveSearch: PropTypes.bool.isRequired,
  onItemClick: PropTypes.func.isRequired,
  showPopup: PropTypes.bool,
  setShowPopup: PropTypes.func,
  handleFormReset: PropTypes.func,
};

export default AdvFormHeader;
